import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';
import MiscUtils from 'services/MiscUtils';
import StaffUtils from 'services/StaffUtils';
import AdminUtils from 'services/AdminUtils';
import { PatientDocument, Admin, Physician, DocumentTemplateCategory } from 'generated/api';

interface DocumentTemplate {
  category_id?: string;
  [key: string]: any;
}

class DocumentUtils {
  OPTIONS_FILL = {
    nothing: 'Nothing',
    task: 'Create Task for Admins',
    approve: 'Document should be approved',
  } as const;

  FAX_STATUS = {
    failed: 'SendingFailed',
    queued: 'Queued',
    sent: 'Sent',
    submitted: 'Submitted',
  } as const;

  DOCUMENT_FROM_OPTIONS = [
    'Care& Family Health',
    'External',
    'True North Imaging',
    'Charm Fertility Clinic',
    'St. Michael\u2019s Hospital',
    'UHN',
    'Sunnybrook',
  ] as const;

  // Server-stamped source of HL7-imported Dynacare lab results (never one of the
  // manually selectable DOCUMENT_FROM_OPTIONS above), so `from === 'Dynacare'`
  // is a collision-free discriminant for a Dynacare-sourced document.
  DOCUMENT_SOURCE = {
    dynacare: 'Dynacare',
  } as const;

  DOCUMENT_TYPE = {
    diagnostic: 'Diagnostic Result',
    diagnostic_requisition: 'Diagnostic Requisition',
    consult_note: 'Consult Note',
    pharmacy_authorization: 'Pharmacy Authorization Request',
    demographics: 'Demographics',
    immunization_record: 'Immunization Record',
    appointment_notification: 'Appointment Notification',
    historical_records: 'Historical Records',
    referral: 'Referral',
    incoming_referral: 'Incoming Referral',
    template: 'Template',
    other: 'Other',
    export: 'Export',
  } as const;

  DOCUMENT_TITLES = {
    [this.DOCUMENT_TYPE.diagnostic!]: [
      'Hematology/Microbiology',
      'Pap',
      'HPV',
      'HIV',
      'ECG',
      'COVID-19 Result',
      'Imaging - Specialized Test',
      'Imaging - Ultrasound',
      'Imaging - Xray',
      'Imaging - MRI',
      'Imaging - CT',
      'Mammogram',
      'Colonoscopy',
      'Blood Type',
      'Fecal Immunochemical Test (FIT)',
      'Bone Mineral Density',
    ],
    [this.DOCUMENT_TYPE.consult_note!]: ['Specialty: X and Physician/provider: X'],
    [this.DOCUMENT_TYPE.pharmacy_authorization!]: ['Pharmacy Authorization Request'],
    [this.DOCUMENT_TYPE.demographics!]: [
      'Patient Driver\u2019s License',
      'Permanent Residency Card',
      'Patient Passport',
      'Patient Health Card',
      'Patient Refugee Status',
    ],
    [this.DOCUMENT_TYPE.immunization_record!]: ['Immunization Record', 'Proof of Immunization'],
    [this.DOCUMENT_TYPE.appointment_notification!]: [
      'Appointment Notification',
      'No-Show for Appointment',
      'Cancelled Appointment',
    ],
    [this.DOCUMENT_TYPE.referral!]: [
      'Referral Acknowledgment',
      'Additional Information Required for Referral',
      'Declined Referral',
    ],
  } as const;

  getTemplateMatchingDocumentCategory(
    template: DocumentTemplate | undefined,
    templateCategories: DocumentTemplateCategory[],
  ): string | undefined {
    if (!template) return;
    let category = templateCategories.find(({ id }) => template?.category_id === id)?.name;
    if (category === 'Referrals') return this.DOCUMENT_TYPE.referral;
    if (category === 'Requisitions') return this.DOCUMENT_TYPE.diagnostic_requisition;
  }

  isAssigned(document: PatientDocument): boolean {
    return !!document.patient_id && !!document.physician_id && !!document.document_type;
  }

  isInboundFax(document: PatientDocument): boolean {
    return !!document.fax_id || document.route === 'Fax';
  }

  isDynacare(document: Pick<PatientDocument, 'from'>): boolean {
    return document.from === this.DOCUMENT_SOURCE.dynacare;
  }

  /**
   * Documents whose patient is assigned by hand (because auto-matching failed
   * or they were faxed in) should NOT auto-fill the patient's primary
   * physician on that manual assignment. Two cases:
   *  - Faxed-in diagnostics (e.g. Dynacare rough copies) duplicate results the
   *    provider already received electronically.
   *  - Dynacare HL7 imports that failed to auto-match: an admin assigns the
   *    patient manually, and the NP is then chosen deliberately rather than
   *    defaulted to the primary — otherwise the lab silently lands in the wrong
   *    provider's pending queue.
   * In both cases the NP is left unset for explicit selection. A Dynacare
   * result that auto-matched at ingest already carries its physician_id and is
   * untouched, since this only gates the client-side primary-physician default.
   */
  shouldSkipPrimaryPhysicianAutofill(document: PatientDocument): boolean {
    const isFaxedDiagnostic =
      this.isInboundFax(document) &&
      document.document_type === this.DOCUMENT_TYPE.diagnostic;
    return isFaxedDiagnostic || this.isDynacare(document);
  }

  isLocked(document: Pick<PatientDocument, 'date'>): boolean {
    if (!document?.date) return false;
    return document.date > moment().endOf('day').unix();
  }

  isLockedForPatient(document: Pick<PatientDocument, 'date'>, role: string | undefined): boolean {
    return role === 'patient' && this.isLocked(document);
  }

  getUnlockDateText(document: Pick<PatientDocument, 'date'>): string {
    if (!document?.date) return '';
    return moment.unix(document.date).format('Do MMM YYYY');
  }

  diagnosticTestsMatch(
    a: Pick<PatientDocument, 'results_text'>,
    b: Pick<PatientDocument, 'results_text'>,
  ): boolean {
    const testNames = (doc: Pick<PatientDocument, 'results_text'>) =>
      new Set(
        (doc.results_text || [])
          .map((line) => line.test?.trim().toLowerCase())
          .filter((test): test is string => !!test),
      );
    const namesA = testNames(a);
    const namesB = testNames(b);
    if (namesA.size === 0 || namesA.size !== namesB.size) return false;
    return [...namesA].every((test) => namesB.has(test));
  }

  getRecentDuplicateDiagnostics(
    documents: PatientDocument[] | undefined,
    currentDoc: Pick<PatientDocument, 'id'>,
    nowUnix: number = moment().unix(),
  ): PatientDocument[] {
    const cutoff = nowUnix - 7 * 24 * 60 * 60; // date is unix seconds (see isLocked)
    return (documents || [])
      .filter(
        (doc) =>
          doc.id !== currentDoc.id &&
          doc.document_type === this.DOCUMENT_TYPE.diagnostic &&
          !doc.archived &&
          (doc.date ?? 0) >= cutoff,
      )
      .sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  }

  filter(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    type?: string,
    targetStatus?: string,
  ): PatientDocument[] {
    return this.toArray(documents).filter(({ document_type, status }) => {
      if (type !== undefined && type !== document_type) return false;
      return targetStatus === undefined || status === targetStatus;
    });
  }

  filterArchived(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    value: boolean,
  ): PatientDocument[] {
    return this.toArray(documents).filter((document) => {
      let isArchived = document.archived || false;
      return isArchived === value;
    });
  }

  filterAssigned(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    assigned: boolean,
  ): PatientDocument[] {
    return this.toArray(documents).filter(({ patient_id }) =>
      assigned ? patient_id !== undefined : patient_id === undefined,
    );
  }

  filterOthers(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    assigned: boolean,
  ): PatientDocument[] {
    return this.filterAssigned(documents, assigned).filter((document) => {
      return (
        document.document_type !== this.DOCUMENT_TYPE.diagnostic &&
        document.document_type !== this.DOCUMENT_TYPE.referral
      );
    });
  }

  filterPatient(
    documents: PatientDocument[] | Record<string, PatientDocument>,
    patientId: string,
  ): PatientDocument[] {
    return this.toArray(documents).filter((document) => {
      return document.patient_id === patientId;
    });
  }

  toArray(documents: PatientDocument[] | Record<string, PatientDocument> = []): PatientDocument[] {
    if (Array.isArray(documents)) {
      return order(documents.filter((item) => item.id !== undefined));
    } else {
      return order(Object.values(documents).filter((item) => item.id !== undefined));
    }
  }

  getFlagsAndCommentsAlert(
    document: PatientDocument,
    role: string = '',
    flagsText: string = '',
    physicians: Record<string, Physician>,
    admins: Record<string, Admin>,
  ): string {
    let text = flagsText;

    if (document.comments) {
      text += `${text ? '\n\n' : ''}Notes for Patient:\n${document.comments}`;
    }
    if (role !== 'patient' && (document as any).general_comments?.length > 0) {
      const assistant = AdminUtils.getAssistantUser(admins);
      let comments = (document as any).general_comments
        .filter((comment: any) => comment.author_id !== assistant?.id)
        .map((comment: any) => {
          const authorId = comment.author_id;
          const staff = physicians[authorId] || admins[authorId];
          const name = staff ? StaffUtils.getName(staff) : authorId;
          return `${name}: ${comment.text}`;
        })
        .join('\n\n');
      if (comments) text += `${text ? '\n\n' : ''}Private Staff Comments:\n${comments}`;
    }

    return text;
  }

  async getFileUrl(
    getFile: (opts: { errorTitle: string }) => Promise<any>,
    getFileInfo?: () => Promise<any>,
  ): Promise<{ fileUrl: string | undefined; type: string; originalUrl?: string }> {
    let fileUrl: string | undefined, originalUrl: string | undefined;
    let type = 'application/pdf';

    if (!!getFileInfo) {
      try {
        const result = await getFileInfo();
        if (result) {
          let blob = await MiscUtils.urlToBlob(result.url, result.type);
          originalUrl = result.url;
          fileUrl = MiscUtils.getBlobUrl(blob);
          type = blob.type;
          fileUrl = MiscUtils.getBlobUrl(blob);
        }
      } catch (err) {
        console.log(err);
        fileUrl = undefined;
      }
    }

    if (!fileUrl) {
      const result = await getFile({ errorTitle: 'Resource Preview' });
      if (result) {
        type = result.type;
        fileUrl = MiscUtils.getBlobUrl(result);
      }
    }
    return { fileUrl, type, originalUrl };
  }
}

function order(documents: PatientDocument[]): PatientDocument[] {
  return PerformanceUtils.sortBy(documents, ['date']);
}

export default new DocumentUtils();
